<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import BulkActionsAPI from 'dashboard/api/bulkActions';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const { t } = useI18n();
const store = useStore();
const labels = useMapGetter('labels/getLabels');

const dialogRef = ref(null);
const fileInput = ref(null);
const mode = ref('paste');
const pastedPhoneNumbers = ref('');
const filePhoneNumbers = ref([]);
const selectedLabels = ref([]);
const labelSearch = ref('');
const selectedFileName = ref('');
const isSubmitting = ref(false);

const normalizedHeader = value =>
  value
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '');

const phoneHeaderNames = new Set([
  'telefone',
  'tel',
  'celular',
  'whatsapp',
  'phone',
  'phone_number',
  'numero',
  'numero_telefone',
]);

const splitRow = row => row.split(/[;,\t]/).map(value => value.trim());
const looksLikePhone = value => {
  const digitCount = value.replace(/\D/g, '').length;
  return digitCount >= 10 && digitCount <= 15;
};

const extractPhoneNumbers = text => {
  const rows = text
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .map(row => row.trim())
    .filter(Boolean);
  if (!rows.length) return [];

  const firstRow = splitRow(rows[0]);
  const phoneColumn = firstRow.findIndex(value =>
    phoneHeaderNames.has(normalizedHeader(value))
  );

  const values =
    phoneColumn >= 0
      ? rows.slice(1).map(row => splitRow(row)[phoneColumn] || '')
      : rows.flatMap(row => splitRow(row).filter(looksLikePhone));

  return [...new Set(values.map(value => value.trim()).filter(Boolean))];
};

const pastedNumbers = computed(() =>
  extractPhoneNumbers(pastedPhoneNumbers.value)
);
const phoneNumbers = computed(() =>
  mode.value === 'paste' ? pastedNumbers.value : filePhoneNumbers.value
);
const filteredLabels = computed(() => {
  const search = labelSearch.value.trim().toLowerCase();
  if (!search) return labels.value;
  return labels.value.filter(label =>
    label.title.toLowerCase().includes(search)
  );
});
const canSubmit = computed(
  () => phoneNumbers.value.length > 0 && selectedLabels.value.length > 0
);

const reset = () => {
  mode.value = 'paste';
  pastedPhoneNumbers.value = '';
  filePhoneNumbers.value = [];
  selectedLabels.value = [];
  labelSearch.value = '';
  selectedFileName.value = '';
  if (fileInput.value) fileInput.value.value = '';
};

const open = async () => {
  reset();
  if (!labels.value.length) await store.dispatch('labels/get');
  dialogRef.value?.open();
};

const toggleLabel = title => {
  selectedLabels.value = selectedLabels.value.includes(title)
    ? selectedLabels.value.filter(label => label !== title)
    : [...selectedLabels.value, title];
};

const chooseFile = () => fileInput.value?.click();

const handleFileChange = async event => {
  const file = event.target.files?.[0];
  if (!file) return;

  selectedFileName.value = file.name;
  filePhoneNumbers.value = extractPhoneNumbers(await file.text());
};

const submit = async () => {
  if (!canSubmit.value || isSubmitting.value) return;

  isSubmitting.value = true;
  try {
    const { data } = await BulkActionsAPI.create({
      type: 'Contact',
      phone_numbers: phoneNumbers.value,
      labels: { add: selectedLabels.value },
    });

    if (data.matched_count > 0) {
      useAlert(
        t('CONTACTS_BULK_ACTIONS.PHONE_IMPORT.SUCCESS', {
          matched: data.matched_count,
          notFound: data.not_found_count,
        })
      );
      dialogRef.value?.close();
    } else {
      useAlert(t('CONTACTS_BULK_ACTIONS.PHONE_IMPORT.NO_MATCHES'));
    }
  } catch (error) {
    useAlert(t('CONTACTS_BULK_ACTIONS.PHONE_IMPORT.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    :title="t('CONTACTS_BULK_ACTIONS.PHONE_IMPORT.TITLE')"
    :confirm-button-label="t('CONTACTS_BULK_ACTIONS.PHONE_IMPORT.CONFIRM')"
    :disable-confirm-button="!canSubmit"
    :is-loading="isSubmitting"
    overflow-y-auto
    @confirm="submit"
  >
    <template #description>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('CONTACTS_BULK_ACTIONS.PHONE_IMPORT.DESCRIPTION') }}
      </p>
    </template>

    <div class="flex flex-col gap-5">
      <div class="grid grid-cols-2 gap-2">
        <Button
          :label="t('CONTACTS_BULK_ACTIONS.PHONE_IMPORT.PASTE')"
          icon="i-lucide-clipboard-list"
          :variant="mode === 'paste' ? 'solid' : 'outline'"
          @click="mode = 'paste'"
        />
        <Button
          :label="t('CONTACTS_BULK_ACTIONS.PHONE_IMPORT.FILE')"
          icon="i-lucide-file-up"
          :variant="mode === 'file' ? 'solid' : 'outline'"
          @click="mode = 'file'"
        />
      </div>

      <div v-if="mode === 'paste'" class="flex flex-col gap-2">
        <label
          for="bulk-contact-phone-numbers"
          class="text-sm font-medium text-n-slate-12"
        >
          {{ t('CONTACTS_BULK_ACTIONS.PHONE_IMPORT.NUMBERS_LABEL') }}
        </label>
        <textarea
          id="bulk-contact-phone-numbers"
          v-model="pastedPhoneNumbers"
          rows="7"
          class="w-full resize-y rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          :placeholder="
            t('CONTACTS_BULK_ACTIONS.PHONE_IMPORT.NUMBERS_PLACEHOLDER')
          "
        />
      </div>

      <div v-else class="flex flex-col gap-2">
        <button
          type="button"
          class="flex min-h-28 flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-n-weak bg-n-alpha-black2 px-4 py-5 text-center hover:bg-n-alpha-2"
          @click="chooseFile"
        >
          <span class="i-lucide-file-up size-5 text-n-slate-11" />
          <span class="text-sm font-medium text-n-slate-12">
            {{
              selectedFileName ||
              t('CONTACTS_BULK_ACTIONS.PHONE_IMPORT.CHOOSE_FILE')
            }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('CONTACTS_BULK_ACTIONS.PHONE_IMPORT.FILE_HELP') }}
          </span>
        </button>
        <input
          ref="fileInput"
          type="file"
          class="hidden"
          accept=".csv,.txt,text/csv,text/plain"
          @change="handleFileChange"
        />
      </div>

      <p class="mb-0 text-xs text-n-slate-11">
        {{
          t('CONTACTS_BULK_ACTIONS.PHONE_IMPORT.NUMBERS_FOUND', {
            count: phoneNumbers.length,
          })
        }}
      </p>

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CONTACTS_BULK_ACTIONS.PHONE_IMPORT.LABELS_LABEL') }}
        </label>
        <Input
          v-model="labelSearch"
          type="search"
          :placeholder="t('CONTACTS_BULK_ACTIONS.PHONE_IMPORT.LABEL_SEARCH')"
        />
        <div
          class="max-h-48 overflow-y-auto rounded-lg border border-n-weak bg-n-alpha-black2 p-2"
        >
          <button
            v-for="label in filteredLabels"
            :key="label.id"
            type="button"
            class="mb-2 mr-2 inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm"
            :class="
              selectedLabels.includes(label.title)
                ? 'border-n-brand bg-n-brand/10 text-n-brand'
                : 'border-n-weak bg-n-solid-1 text-n-slate-11 hover:border-n-brand'
            "
            @click="toggleLabel(label.title)"
          >
            <span
              class="size-2 rounded-full"
              :style="{ backgroundColor: label.color }"
            />
            {{ label.title }}
            <span
              v-if="selectedLabels.includes(label.title)"
              class="i-lucide-check size-3"
            />
          </button>
          <p v-if="!filteredLabels.length" class="m-2 text-sm text-n-slate-11">
            {{ t('CONTACTS_BULK_ACTIONS.NO_LABELS_FOUND') }}
          </p>
        </div>
      </div>
    </div>
  </Dialog>
</template>
